import { authFetchPOST, authFetchGET } from '../utils/auth-fetch';
import { WeatherModel } from '../models/weather-model';
import { VoyageRoute, RoutingJobOptions, VoyageRoutePointFeature, NextWaypointPayload, NextWaypointResult, SubscriptionInfo, WeatherLimits, RouteGeometry, WeatherSummary } from '../models/voyage-routing-model';
import { VoyagePlan } from '../models/vessel-planning-model';
import { VoyageImoModel } from '../models/voyage-imo-model';

const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;
const routeOptimiserApi = (<any>window)["appenv"].services.routeoptimiserapi;
const env = { ...routeOptimiserApi, ...isVesselOnCloud ? { scope: (<any>window)["appenv"].scope } : {} };

interface InputRoute
{
    imo: number;
    forecast: string;
    etd: string;
    eta: string;
    draughtFore: number;
    draughtAft: number;
    reeferCount: number;
    weatherLimits: WeatherLimits;
    routeGeometry: RouteGeometry;
    scheduleId: string;
    gmt?: number;
    min_depth?: number;
    catzoc?: number;
    max_x_track?: number;
    rot?: number;
    solidGm?: number;
    whaleZonesApplied?: boolean;
    disableWhaleZoneSpeedRestrictions?: boolean;
}

interface CalculatedRoute
{
    forecast: string;
    forecastHasLowResData: boolean;
    forecastHasHighResData: boolean;
    subscriptions: { id: string; importedAt: string; }[];
    etd: string;
    eta: string;
    reeferCount: number;
    reeferConsumption: number;
    draughtFore: number;
    draughtAft: number;
	gmt?: number;
	routeGeometry: RouteGeometry;
	weatherLimits: WeatherLimits;
    weatherSummary: {[index:string]: WeatherSummary};
    totals: {
        fuelConsumption: number;
        fuelConsumptionLower?: number;
        fuelConsumptionUpper?: number;
        distance: number;
        speedOverGround: number;
    };
    hydraRequestId: string;
    hydraErrors: null|any[];
    warnings?: string[];
}

export interface RouteChecksSummary
{
    totalStarRoutes: number;
    heavyWeatherPassed: number;
    parametricRollPassed: number;
    ukcPassed: number;
}

export interface BprJobErrorDetails
{
    routeChecks?: RouteChecksSummary;
    hydraErrorBody?: { message?: string };
    oorError?: string;
    [index: string]: any;
}

interface RouteOptimiserResult
{
    id: number;
    createdAt: string;
    updatedAt: string;
    status: string;
    statusDetails?: string;
    errorCode?: number;
    errorDetails?: {[index:string]:any};
    inputRoute: InputRoute;
    calculatedRoute?: CalculatedRoute;
}

export interface RouteOptimiserJobResult extends RouteOptimiserResult
{
    route?: VoyageRoute
}

export interface StageEstimate
{
    stage: string;
    startedAt: string;
    expectedSeconds?: number;
}

export interface BprJobResult
{
    id: string;
    status: string;
    statusDetails?: string;
    errorDetails?: {[index:string]:any};
    stageEstimate?: StageEstimate;
}

export interface OORReadiness
{
    imo: number;
    lastSuccessfulCalculationAt?: string;
}

// The routeoptimiser backend requires every LineString segment's leg_type to survive
// through to its power calculation call, and rejects the whole job with a 400 if any is
// missing. Log any gap here, right before submission, so it can be traced back to the UI
// flow that produced it instead of only surfacing as an opaque 400 on the backend.
export function logMissingLegTypeSegments(routeGeometry: RouteGeometry, scheduleId?: string)
{
    routeGeometry.forEach((feature, index) =>
    {
        if (feature.geometry.type === "LineString" && !feature.properties.leg_type)
        {
            console.error('Route geometry LineString is missing leg_type before submitting manual route calculation job', {
                index,
                scheduleId,
                coordinates: feature.geometry.coordinates,
                properties: feature.properties,
            });
        }
    });
}

export const RouteOptimizerService = new class extends EventTarget
{
    async getNextWaypointIndex(payload: NextWaypointPayload) : Promise<NextWaypointResult>{
        const result = await authFetchPOST(env.scope, `${env.baseurl}/v1/calculate-next-waypoint`, payload);

        return result;
    }

    async postCalculationJob(route: VoyageRoute, options: RoutingJobOptions): Promise<number>
    {
        const draughtFore = options.draftFore!;
        const draughtAft = options.draftAft!;
        const minDepth = options.min_depth!;
        const catZoc = options.catzoc!;
        const maxXTrack = options.max_x_track!;
        const rateOfTurn = options.rate_of_turn!;
        const turnRadius = options.turn_radius!;
        const reeferCount = options.numberOfReefers!;
        const forecast = options.forecastVersion || WeatherModel!.latestForecastVersion;
        const etd = options.etd || Date.now();
        const routeGeometry = JSON.parse(JSON.stringify(route.route_geometry));
        logMissingLegTypeSegments(routeGeometry, options.planScheduleId);
        const weatherLimits = options.weatherLimits;
        const rollAngle = options.rollAngle !== undefined ? options.rollAngle : null;
        const solidGm = options.solidGm !== undefined ? options.solidGm : null;
        
        const payload = {
            inputRoute: {
                imo: isVesselOnCloud ? VoyageImoModel!.imo! : (<any>window)['appenv'].vesselIMONumber,
                forecast,
                etd: new Date(etd).toISOString(),
                eta: new Date(options.eta!).toISOString(),
                draughtFore,
                draughtAft,
                reeferCount,
                weatherLimits,
                routeGeometry,
                scheduleId: options.planScheduleId,
                gmt: options.gmt,
                minDepth,
                catZoc,
                maxXTrack,
                rateOfTurn,
                turnRadius,
                rollAngle: rollAngle !== null ? rollAngle : undefined, // Only include if not null
                solidGm: solidGm !== null ? solidGm : undefined, // Only include if not null
                whaleZonesApplied: options.whaleZonesApplied ?? false,
                // Only sent when the user disabled the whale zone speed restrictions. The
                // routeoptimiser turns it into zones_to_ignore: ["WHALE"] on the constant-power
                // request; left undefined it is dropped by JSON.stringify and analytics keeps
                // applying the restrictions, which is the default behaviour.
                disableWhaleZoneSpeedRestrictions: options.disableWhaleZoneSpeedRestrictions ? true : undefined,
            }
        };

        const postResult = await authFetchPOST(env.scope, `${env.baseurl}/v1/calculations`, payload);
        if (!postResult || !postResult.id)
        {
            console.warn('Unknown response from route optimiser API:', postResult);
            throw new Error("Unknown response from route optimiser API");
        }

        return <number>postResult.id;
    }

    async waitForJob(jobId: number, options: RoutingJobOptions): Promise<RouteOptimiserJobResult>
    {
        let status = "pending";
        let result: RouteOptimiserJobResult|null = null;
        while (status === "pending")
        {
            result = <RouteOptimiserResult>await authFetchGET(env.scope, `${env.baseurl}/v1/calculations/${jobId}`);
            status = result.status ? result.status : "failed";
            if (status === "pending")
            {
                await new Promise(resolve => setTimeout(resolve, 1000)); // Wait one second between polls
            }
        }

        if (status !== "complete")
        {
            return result!;
        }

        // Convert to VoyageRoute format
        const endpoint = <VoyageRoutePointFeature>result!.calculatedRoute!.routeGeometry[result!.calculatedRoute!.routeGeometry.length-1];
        const duration = new Date(endpoint.properties.timestamp!).getTime() - new Date(result!.calculatedRoute!.etd).getTime();

        const subscriptions: SubscriptionInfo[] = [];
        if (result!.calculatedRoute!.subscriptions)
        {
            for (const subInfo of result!.calculatedRoute!.subscriptions)
            {
                subscriptions.push({
                    forecast: result!.calculatedRoute!.forecast,
                    id: subInfo.id,
                    importedAt: subInfo.importedAt
                });
            }
        }

        const routeId = result?.route?.routeId ?? '';
        const route: VoyageRoute = {
            routeId: routeId,
            route_geometry: result!.calculatedRoute!.routeGeometry,
            route_info: {
                eta: result!.calculatedRoute!.eta,
                etd: result!.calculatedRoute!.etd,
                speed_over_ground: result!.calculatedRoute!.totals.speedOverGround,
                co2: -1, rpm: -1, // Not available
                totals: {
                    eca: { time: 0, fuel_cost: 0, vessel_cost: 0, distance: 0, fuel: 0 },
                    non_eca: {
                        time: duration/60*60*1000, // convert to hours
                        fuel_cost: 0, vessel_cost: 0,
                        distance: result!.calculatedRoute!.totals.distance,
                        fuel: result!.calculatedRoute!.totals.fuelConsumption
                    }
                },
                weather: {
                    forecast_versions: [ result!.calculatedRoute!.forecast ],
                    subscriptions: subscriptions,
                    summary: result!.calculatedRoute!.weatherSummary,
                    hasLowResData: result!.calculatedRoute!.forecastHasLowResData,
                    hasHighResData: result!.calculatedRoute!.forecastHasHighResData
                },
                calculationInput: {
                    forecast_versions: [ result!.calculatedRoute!.forecast ],
                    draftFore: result!.calculatedRoute!.draughtFore,
                    draftAft: result!.calculatedRoute!.draughtAft,
                    gmt: result!.calculatedRoute!.gmt,
                    etd: result!.calculatedRoute!.etd,
                    eta: result!.calculatedRoute!.eta,
                    weatherLimits: result!.calculatedRoute!.weatherLimits
                },
                hydraRequestId: result!.calculatedRoute!.hydraRequestId,
                hydraErrors: result!.calculatedRoute!.hydraErrors || [],
                warnings: result!.calculatedRoute!.warnings || [],
            }
        }

        result!.route = route;

        return result!;
    }

    async postBPRRankingJob(plan: VoyagePlan, latestForecastVersion: string, BPRIDs: string[], weatherLimits:WeatherLimits): Promise<string | null> {
        const payload = {bprReq: {
            imo_number: plan.imoNo || isVesselOnCloud ? VoyageImoModel?.imo :  (<any>window)['appenv'].vesselIMONumber,
            etd: new Date(plan.etd).toISOString(),
            eta: new Date(plan.eta).toISOString(),
            etdPilot: new Date(plan.departurePilotEtd).toISOString(),
            etaPilot: new Date(plan.arrivalPilotEta).toISOString(),
            departure_terminal: plan.departureTerminalCode,
            arrival_terminal: plan.arrivalTerminalCode,
            draught_fore: plan.foreDraft,
            draught_aft: plan.aftDraft,
            rollAngle: plan.rollAngle || null,
            reefer_consumption: plan.numberOfReefers,
            transverse_metacentric_height: plan.gmt || 0,
            transverse_metacentric_height_solid: plan.solidGm,
            schedule_id: plan.scheduleId,
            forecast: latestForecastVersion,
            // When supplied, the job evaluates these routes. Vessel routing-driver requires route_ids.
            ...(BPRIDs && BPRIDs.length > 0 ? { best_practice_route_ids: BPRIDs } : {}),
            weatherLimits
        }};

        const postResult = await authFetchPOST(env.scope, `${env.baseurl}/v1/bpr`, payload);
        if (!postResult || !postResult.id) {
            console.warn('Unknown response from route optimiser API:', postResult);
            throw new Error("Unknown response from route optimiser API");
        }

        return <string>postResult.id;
    }

    async getOORReadiness(imo: string): Promise<OORReadiness | null>
    {
        return await authFetchGET(env.scope, `${env.baseurl}/v1/bpr/oor-readiness/${imo}`);
    }

    async getResultForRankedBPRJob(jobId: string, isVesselOnCloud: boolean, onPoll?: (result: BprJobResult) => void): Promise<BprJobResult | null>
    {
        let status = "";
        let result: BprJobResult | null = null;
        if (isVesselOnCloud) {
            result = <BprJobResult>await authFetchGET(env.scope, `${env.baseurl}/v1/bpr/${jobId}`);
        } else {
            while (status !== "failed" && status !== "complete" && status !== "failedPendingRetry")
            {
                result = <BprJobResult>await authFetchGET(env.scope, `${env.baseurl}/v1/bpr/${jobId}`);
                status = result.status ? result.status : "Failed";
                onPoll?.(result);
                if (status !== "failed" && status !== "complete" && status !== "failedPendingRetry")
                {
                    await new Promise(resolve => setTimeout(resolve, 2000)); // Wait two seconds between polls
                }
            }
        }
        return result;
    }
};
